package fr.manyverse.app.presentation.screens

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Backpack
import androidx.compose.material.icons.filled.Person
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LifecycleEventEffect
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.CharacterMode
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.ActiveTimer
import fr.manyverse.app.domain.model.AlertType
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.model.DiceFaceStyle
import fr.manyverse.app.domain.model.DieConfig
import fr.manyverse.app.domain.model.DieType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.model.GameModule
import fr.manyverse.app.domain.model.GameTable
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.infrastructure.gamesession.startGameSessionBackground
import fr.manyverse.app.infrastructure.gamesession.stopGameSessionBackground
import fr.manyverse.app.infrastructure.shake.RememberGuardedShakeDetector
import fr.manyverse.app.presentation.components.DraggableBubble
import fr.manyverse.app.presentation.components.common_tabs.DiceShakeOverlay
import fr.manyverse.app.presentation.components.common_tabs.DiceTab
import fr.manyverse.app.presentation.components.common_tabs.MessagesTab
import fr.manyverse.app.presentation.components.modules.announcement.AnnouncementBubble
import fr.manyverse.app.presentation.components.modules.announcement.AnnouncementPlayerOverlay
import fr.manyverse.app.presentation.components.modules.digicode.DigicodeBubble
import fr.manyverse.app.presentation.components.modules.combat.CombatBubble
import fr.manyverse.app.presentation.components.modules.combat.CombatFrieze
import fr.manyverse.app.presentation.components.modules.digicode.DigicodePlayerOverlay
import fr.manyverse.app.presentation.components.modules.lockpicking.LockpickingBubble
import fr.manyverse.app.presentation.components.modules.lockpicking.LockpickingPlayerOverlay
import fr.manyverse.app.presentation.components.modules.timer.TimerBubble
import fr.manyverse.app.presentation.components.modules.whiteboard.WhiteboardBubble
import fr.manyverse.app.presentation.components.modules.whiteboard.WhiteboardPlayerOverlay
import fr.manyverse.app.presentation.components.notification.NotificationContent
import fr.manyverse.app.presentation.components.notification.NotificationHost
import fr.manyverse.app.presentation.components.player_screen.PlayerTableTab
import fr.manyverse.app.presentation.components.player_screen.character.CharacterTab
import fr.manyverse.app.presentation.components.player_screen.inventory.InventoryTab
import fr.manyverse.app.presentation.layout.GameScreenScaffold
import fr.manyverse.app.presentation.layout.NavigationItem
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CombatViewModel
import fr.manyverse.app.presentation.viewmodel.DigicodeViewModel
import fr.manyverse.app.presentation.viewmodel.InventoryViewModel
import fr.manyverse.app.presentation.viewmodel.LockpickingViewModel
import fr.manyverse.app.presentation.viewmodel.PlayerViewModel
import fr.manyverse.app.presentation.viewmodel.TimerViewModel
import fr.manyverse.app.presentation.viewmodel.WhiteboardState
import fr.manyverse.app.presentation.viewmodel.WhiteboardViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character
import manyverse.shared.generated.resources.conversations
import manyverse.shared.generated.resources.dice
import manyverse.shared.generated.resources.icon_backpack_open
import manyverse.shared.generated.resources.icon_dice_1
import manyverse.shared.generated.resources.icon_dice_6
import manyverse.shared.generated.resources.icon_message_in
import manyverse.shared.generated.resources.icon_message_out
import manyverse.shared.generated.resources.icon_open_book
import manyverse.shared.generated.resources.icon_open_page
import manyverse.shared.generated.resources.icon_wizard
import manyverse.shared.generated.resources.inventory
import manyverse.shared.generated.resources.table
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.vectorResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.random.Random

@Immutable
private data class ShakeRoll(
    val dieConfig: DieConfig,
    val count: Int,
    val modifier: Int,
    val characterName: String,
    /** Identifie un lancer unique : force le remontage de l'overlay à chaque secouage. */
    val rollId: Long = Random.nextLong()
)

@Immutable
private data class PlayerDiceSelection(val dieConfig: DieConfig, val count: Int, val modifier: Int)

private enum class PlayerTab { TABLE, CHARACTER, INVENTORY, CONVERSATIONS, DICE }

@Composable
fun PlayerScreen(
    table: GameTable,
    tableMembers: List<TableMember>,
    characters: List<Character>,
    universeId: String,
    currentUserId: String,
    onNavigateBack: () -> Unit
) {
    val universeConfig = remember(table.id) { table.resolvedConfig }
    val availableModules = remember(universeConfig) { universeConfig.modules }
    val availableDice = remember(universeConfig) {
        universeConfig.dice.available.map { it.toDieConfig(universeConfig.dice.highValuesWin) }
    }
    val defaultDie = universeConfig.dice.defaultDie
    val defaultDiceCount = universeConfig.dice.defaultCount
    val multiDiceEnabled = universeConfig.dice.multiEnabled
    val modifiersEnabled = universeConfig.dice.modifiersEnabled
    val diceTotalEnabled = universeConfig.dice.totalEnabled
    val diceFaceStyle = universeConfig.dice.faceStyle
    val features = universeConfig.features
    val isFreeConstruction = universeConfig.characterMode == CharacterMode.FREE_CONSTRUCTION
    val isCharacterSheetAvailable = features.characterSheet

    val initialTabIndex = if (characters.none { it.userId == currentUserId }) 1 else 0
    var selectedTabIndex by remember { mutableIntStateOf(initialTabIndex) }
    var shakeRollResult by remember { mutableStateOf<ShakeRoll?>(null) }
    // Les dés de l'overlay roulent encore : un nouveau secouage ne doit pas les couper.
    var shakeRollRolling by remember { mutableStateOf(false) }
    var diceSelection by remember { mutableStateOf<PlayerDiceSelection?>(null) }
    // Conversation à ouvrir dans l'onglet Conversations (appui sur une notification de message)
    var requestedConversationKey by remember { mutableStateOf<String?>(null) }

    val viewModel: PlayerViewModel =
        koinViewModel { parametersOf(table.id, currentUserId, availableDice) }
    val activeAnnouncements by viewModel.activeAnnouncements.collectAsStateWithLifecycle()
    val minimizedAnnouncementIds by viewModel.minimizedAnnouncementIds.collectAsStateWithLifecycle()
    val notifications by viewModel.notifications.collectAsStateWithLifecycle()
    val messages by viewModel.messages.collectAsStateWithLifecycle()
    val unreadMessageCount by viewModel.unreadMessageCount.collectAsStateWithLifecycle()
    val isKicked by viewModel.isKicked.collectAsStateWithLifecycle()
    val isLeft by viewModel.isLeft.collectAsStateWithLifecycle()

    val diceConfigState by viewModel.savedDiceConfig.collectAsStateWithLifecycle()
    val storedDice = (diceConfigState as? AsyncUiState.Success)?.data
    val diceConfigLoaded = diceConfigState !is AsyncUiState.Loading
    val effectiveDefaultDie =
        storedDice?.let { s -> availableDice.firstOrNull { it.type.name == s.dieType }?.type } ?: defaultDie
    val effectiveDefaultCount = storedDice?.count?.takeIf { multiDiceEnabled } ?: defaultDiceCount
    val effectiveDefaultModifier = storedDice?.modifier?.takeIf { modifiersEnabled } ?: 0

    LaunchedEffect(diceConfigState) {
        val s = storedDice ?: return@LaunchedEffect
        val cfg = availableDice.firstOrNull { it.type.name == s.dieType } ?: return@LaunchedEffect
        diceSelection = PlayerDiceSelection(cfg, effectiveDefaultCount, effectiveDefaultModifier)
    }

    LaunchedEffect(isKicked) {
        if (isKicked) onNavigateBack()
    }

    LaunchedEffect(isLeft) {
        if (isLeft) onNavigateBack()
    }

    // Whiteboard ViewModel
    val whiteboardViewModel: WhiteboardViewModel = koinViewModel()
    val whiteboardState by whiteboardViewModel.whiteboardState.collectAsStateWithLifecycle()

    // Combat ViewModel
    val combatViewModel: CombatViewModel = koinViewModel()
    val combatState by combatViewModel.state.collectAsStateWithLifecycle()

    // Timer ViewModel
    val timerViewModel: TimerViewModel = koinViewModel()
    val playerActiveTimers by timerViewModel.activeTimers.collectAsStateWithLifecycle()

    // Shake-to-roll
    // Le secouage ne fait qu'ouvrir l'overlay : le résultat vient des dés, qui le publieront
    // eux-mêmes en se couchant.
    RememberGuardedShakeDetector(
        // Univers sans dés : pas d'onglet Dés, donc pas de lancer au secouage non plus.
        enabled = features.dice,
        onShake = {
            // Tant que les dés n'ont pas donné leur résultat, secouer ne relance rien.
            if (shakeRollRolling) return@RememberGuardedShakeDetector
            val selection = diceSelection
            val dieConfig = selection?.dieConfig ?: availableDice.firstOrNull { it.type == defaultDie }
            val count = selection?.count ?: defaultDiceCount
            val rollModifier = selection?.modifier ?: 0
            val characterName = characters.firstOrNull { it.userId == currentUserId }?.name
            if (dieConfig != null && characterName != null) {
                shakeRollResult = ShakeRoll(dieConfig, count, rollModifier, characterName)
                shakeRollRolling = true
            }
        }
    )

    // Digicode ViewModel
    val digicodeViewModel: DigicodeViewModel = koinViewModel()
    val playerActiveDigicode by digicodeViewModel.activeDigicode.collectAsStateWithLifecycle()
    val digicodeSubmitState by digicodeViewModel.submitState.collectAsStateWithLifecycle()
    val isDigicodeMinimized by digicodeViewModel.isMinimized.collectAsStateWithLifecycle()

    // Lockpicking ViewModel
    val lockpickingViewModel: LockpickingViewModel = koinViewModel()
    val playerActiveLockpicking by lockpickingViewModel.activeLockpicking.collectAsStateWithLifecycle()
    val isLockpickingMinimized by lockpickingViewModel.isMinimized.collectAsStateWithLifecycle()

    LaunchedEffect(table.id) {
        if (GameModule.WHITEBOARD in availableModules) {
            whiteboardViewModel.startObservingActiveWhiteboards(table.id, currentUserId)
        }
        if (GameModule.TIMER in availableModules) {
            timerViewModel.startObservingTimers(table.id, currentUserId)
        }
        if (GameModule.DIGICODE in availableModules) {
            digicodeViewModel.startObservingForPlayer(table.id, currentUserId)
        }
        if (GameModule.LOCKPICKING in availableModules) {
            lockpickingViewModel.startObservingForPlayer(table.id, currentUserId)
        }
        if (GameModule.COMBAT in availableModules) {
            combatViewModel.startObservingActiveCombat(table.id, currentUserId)
        }
    }

    // Play sounds when a digicode attempt is processed
    val digicodeSeenAttemptCounts = remember { mutableMapOf<String, Int>() }
    LaunchedEffect(playerActiveDigicode?.id, playerActiveDigicode?.attempts?.size) {
        if (GameModule.DIGICODE !in availableModules) return@LaunchedEffect
        val digicode = playerActiveDigicode ?: return@LaunchedEffect
        val currentSize = digicode.attempts.size
        val previousSize = digicodeSeenAttemptCounts[digicode.id]
        digicodeSeenAttemptCounts[digicode.id] = currentSize
        if (previousSize == null || currentSize <= previousSize) return@LaunchedEffect
        val lastAttempt = digicode.attempts.lastOrNull() ?: return@LaunchedEffect
        if (!digicode.soundEnabled) return@LaunchedEffect
        if (lastAttempt.isSuccess) {
            viewModel.playEffect("files/digicode_success.mp3", "digicode_success")
        } else {
            viewModel.playEffect("files/digicode_failure.mp3", "digicode_failure")
        }
    }

    // Play sounds when a lock is unlocked
    val lockpickingSeenUnlockedCounts = remember { mutableMapOf<String, Int>() }
    LaunchedEffect(playerActiveLockpicking?.id, playerActiveLockpicking?.unlockedCount) {
        if (GameModule.LOCKPICKING !in availableModules) return@LaunchedEffect
        val lockpicking = playerActiveLockpicking ?: return@LaunchedEffect
        val currentCount = lockpicking.unlockedCount
        val previousCount = lockpickingSeenUnlockedCounts[lockpicking.id]
        lockpickingSeenUnlockedCounts[lockpicking.id] = currentCount
        if (previousCount == null || currentCount <= previousCount) return@LaunchedEffect
        if (!lockpicking.soundEnabled) return@LaunchedEffect
        viewModel.playEffect("files/lockpicking_success.mp3", "lockpicking_success")
    }

    val currentCharacter = characters.firstOrNull { it.userId == currentUserId }
    val hasCharacter = currentCharacter != null

    val visibleTabs = buildList {
        add(PlayerTab.TABLE)
        add(PlayerTab.CHARACTER)
        if (features.inventory && hasCharacter) add(PlayerTab.INVENTORY)
        if (features.messages && hasCharacter) add(PlayerTab.CONVERSATIONS)
        if (features.dice && hasCharacter) add(PlayerTab.DICE)
    }
    val navigationItems = visibleTabs.map { tab ->
        when (tab) {
            PlayerTab.TABLE -> NavigationItem(
                label = stringResource(Res.string.table),
                icon = vectorResource(Res.drawable.icon_open_book),
                selectedIcon = vectorResource(Res.drawable.icon_open_page)
            )
            PlayerTab.CHARACTER -> NavigationItem(
                label = stringResource(Res.string.character),
                icon = Icons.Default.Person,
                selectedIcon = vectorResource(Res.drawable.icon_wizard)
            )
            PlayerTab.INVENTORY -> NavigationItem(
                label = stringResource(Res.string.inventory),
                icon = Icons.Default.Backpack,
                selectedIcon = vectorResource(Res.drawable.icon_backpack_open)
            )
            PlayerTab.CONVERSATIONS -> NavigationItem(
                label = stringResource(Res.string.conversations),
                icon = vectorResource(Res.drawable.icon_message_out),
                selectedIcon = vectorResource(Res.drawable.icon_message_in),
                badgeCount = unreadMessageCount
            )
            PlayerTab.DICE -> NavigationItem(
                label = stringResource(Res.string.dice),
                icon = vectorResource(Res.drawable.icon_dice_1),
                selectedIcon = vectorResource(Res.drawable.icon_dice_6)
            )
        }
    }
    val conversationsTabIndex = visibleTabs.indexOf(PlayerTab.CONVERSATIONS)
    val inventoryTabIndex = visibleTabs.indexOf(PlayerTab.INVENTORY)

    LaunchedEffect(visibleTabs.size) {
        if (selectedTabIndex >= visibleTabs.size) selectedTabIndex = 0
    }

    DisposableEffect(Unit) {
        startGameSessionBackground()
        onDispose { stopGameSessionBackground() }
    }

    // Rattrape ce que le temps réel n'a pas transmis : appli en arrière-plan ou coupure réseau
    fun resync() {
        viewModel.refreshOnForeground()
        if (GameModule.WHITEBOARD in availableModules) {
            whiteboardViewModel.refreshOnForeground(table.id, currentUserId)
        }
        if (GameModule.TIMER in availableModules) {
            timerViewModel.refreshOnForeground(table.id, currentUserId)
        }
        if (GameModule.DIGICODE in availableModules) {
            digicodeViewModel.refreshForPlayer(table.id, currentUserId)
        }
        if (GameModule.LOCKPICKING in availableModules) {
            lockpickingViewModel.refreshForPlayer(table.id, currentUserId)
        }
        if (GameModule.COMBAT in availableModules) {
            combatViewModel.refreshOnForeground(table.id, currentUserId)
        }
    }

    LifecycleEventEffect(Lifecycle.Event.ON_RESUME) { resync() }

    LaunchedEffect(viewModel) {
        viewModel.reconnections.collect { resync() }
    }

    var navigationPadding by remember { mutableStateOf(PaddingValues()) }

    Box(modifier = Modifier.fillMaxSize()) {
        GameScreenScaffold(
            navigationItems = navigationItems,
            onNavigationPaddingChanged = { navigationPadding = it },
            selectedTabIndex = selectedTabIndex,
            onTabSelected = { index ->
                selectedTabIndex = index
                if (index == conversationsTabIndex) {
                    viewModel.clearUnreadMessages()
                }
            },
            overlayContent = {
                PlayerBubbleOverlays(
                    activeAnnouncements = activeAnnouncements,
                    minimizedAnnouncementIds = minimizedAnnouncementIds,
                    playerActiveDigicode = playerActiveDigicode,
                    isDigicodeMinimized = isDigicodeMinimized,
                    playerActiveLockpicking = playerActiveLockpicking,
                    isLockpickingMinimized = isLockpickingMinimized,
                    whiteboardState = whiteboardState,
                    availableModules = availableModules,
                    combatIsMinimized = combatState.isMinimized,
                    combatActiveParticipant = combatState.combat?.activeParticipant,
                    combatCharacters = characters,
                    onRestoreAnnouncement = { viewModel.restoreAnnouncement(it) },
                    onRestoreDigicode = { digicodeViewModel.restore() },
                    onRestoreLockpicking = { lockpickingViewModel.restore() },
                    onRestoreWhiteboard = { whiteboardViewModel.restore() },
                    onRestoreCombat = { combatViewModel.restore() }
                )
            }
        ) {
            PlayerTabContent(
                selectedTab = visibleTabs.getOrElse(selectedTabIndex) { PlayerTab.TABLE },
                table = table,
                tableMembers = tableMembers,
                characters = characters,
                currentCharacter = currentCharacter,
                currentUserId = currentUserId,
                universeId = universeId,
                isFreeConstruction = isFreeConstruction,
                isCharacterSheetAvailable = isCharacterSheetAvailable,
                messages = messages,
                requestedConversationKey = requestedConversationKey,
                onConversationRequestHandled = { requestedConversationKey = null },
                availableDice = availableDice,
                defaultDie = effectiveDefaultDie,
                multiDiceEnabled = multiDiceEnabled,
                modifiersEnabled = modifiersEnabled,
                totalEnabled = diceTotalEnabled,
                faceStyle = diceFaceStyle,
                defaultDiceCount = effectiveDefaultCount,
                defaultModifier = effectiveDefaultModifier,
                diceConfigLoaded = diceConfigLoaded,
                shakeRollActive = shakeRollResult != null,
                onNavigateBack = onNavigateBack,
                onLeaveTable = { viewModel.leaveTable() },
                onSendMessage = { text, ids, characterName ->
                    viewModel.sendMessage(text, ids, characterName)
                },
                onSendAlert = { gmId, characterName, alertType, message ->
                    viewModel.sendAlert(gmId, characterName, alertType, message)
                },
                onRollCompleted = { dieConfig, count, results, modifier ->
                    val characterName = currentCharacter?.name ?: currentUserId
                    viewModel.createDiceRollEvent(
                        characterName = characterName,
                        dieType = dieConfig.type,
                        count = count,
                        results = results,
                        modifier = modifier
                    )
                },
                onDiceConfigChanged = { dieConfig, count, mod ->
                    diceSelection = PlayerDiceSelection(dieConfig, count, mod)
                    viewModel.saveDiceConfig(dieConfig.type, count, mod)
                }
            )
        }

        // Ordre des overlays (le dernier déclaré passe devant) : contenu persistant (tableau
        // blanc) < ponctuel (annonce, frise de combat) < actions attendues du joueur (énigmes)
        // < retours brefs (notifications, dés) < bulles de minuteurs.

        // Whiteboard overlay (read-only for players)
        WhiteboardPlayerOverlay(
            isVisible = whiteboardState.isOpen,
            strokes = whiteboardState.strokes,
            showGrid = whiteboardState.showGrid,
            onMinimize = { whiteboardViewModel.minimize() }
        )

        // Fullscreen announcement overlay (at most one non-minimized at a time)
        val visibleAnnouncement =
            activeAnnouncements.firstOrNull { it.eventId !in minimizedAnnouncementIds }
        if (visibleAnnouncement != null) {
            // Keyed so a new announcement never inherits the previous one's zoom/text state
            key(visibleAnnouncement.eventId) {
                AnnouncementPlayerOverlay(
                    announcement = visibleAnnouncement,
                    onMinimize = { id -> viewModel.minimizeAnnouncement(id) }
                )
            }
        }

        // Combat order frieze (shown when the player expands the combat bubble)
        combatState.combat?.let { combat ->
            if (combatState.isExpanded) {
                CombatFrieze(
                    combat = combat,
                    characters = characters,
                    onDismiss = { combatViewModel.minimize() }
                )
            }
        }

        // Digicode challenge overlay (hidden when minimized as bubble)
        if (GameModule.DIGICODE in availableModules && !isDigicodeMinimized) {
            playerActiveDigicode?.let { digicode ->
                DigicodePlayerOverlay(
                    digicode = digicode,
                    onSubmit = { attemptValue ->
                        digicodeViewModel.submit(
                            digicode = digicode,
                            attemptValue = attemptValue
                        )
                    },
                    onTimeout = {
                        digicodeViewModel.timeout(digicodeId = digicode.id)
                    },
                    onClose = { digicodeViewModel.close() },
                    onMinimize = { digicodeViewModel.minimize() },
                    isSubmitting = digicodeSubmitState is AsyncUiState.Loading,
                    submitError = (digicodeSubmitState as? AsyncUiState.Error)?.message,
                    modifier = Modifier.fillMaxSize()
                )
            }
        }

        // Lockpicking challenge overlay (hidden when minimized as bubble)
        if (GameModule.LOCKPICKING in availableModules && !isLockpickingMinimized) {
            playerActiveLockpicking?.let { lockpicking ->
                LockpickingPlayerOverlay(
                    lockpicking = lockpicking,
                    onUnlock = {
                        lockpickingViewModel.unlockNext(lockpicking = lockpicking)
                    },
                    onTimeout = {
                        lockpickingViewModel.timeout(lockpickingId = lockpicking.id)
                    },
                    onClose = { lockpickingViewModel.close() },
                    onMinimize = { lockpickingViewModel.minimize() },
                    modifier = Modifier.fillMaxSize()
                )
            }
        }

        // Notifications (dés, messages, objets reçus…) : pleine largeur, en haut
        NotificationHost(
            notifications = notifications,
            onDismiss = { viewModel.dismissNotification(it.id) },
            // Appui sur une notification de message ou d'objet reçu : réduit ce qui masque les
            // onglets (tableau blanc, annonces, combat, énigmes) puis ouvre l'onglet associé.
            onClick = { notif ->
                val targetTabIndex = when (notif) {
                    is NotificationContent.NewMessage -> conversationsTabIndex
                    is NotificationContent.ItemReceived -> inventoryTabIndex
                    else -> -1
                }
                if (targetTabIndex >= 0) {
                    viewModel.dismissNotification(notif.id)
                    if (whiteboardState.isOpen) whiteboardViewModel.minimize()
                    activeAnnouncements
                        .filter { it.eventId !in minimizedAnnouncementIds }
                        .forEach { viewModel.minimizeAnnouncement(it.eventId) }
                    if (combatState.isExpanded) combatViewModel.minimize()
                    if (playerActiveDigicode != null && !isDigicodeMinimized) digicodeViewModel.minimize()
                    if (playerActiveLockpicking != null && !isLockpickingMinimized) lockpickingViewModel.minimize()
                    selectedTabIndex = targetTabIndex
                    if (notif is NotificationContent.NewMessage) {
                        requestedConversationKey = notif.conversationKey
                        viewModel.clearUnreadMessages()
                    }
                }
            },
            modifier = Modifier
                .align(Alignment.TopCenter)
                .statusBarsPadding()
                .fillMaxWidth()
                .padding(start = 12.dp, top = 8.dp, end = 12.dp),
            diceTotalEnabled = diceTotalEnabled
        )

        // Shake-to-roll overlay : rien à afficher si l'univers n'a pas les dés.
        shakeRollResult?.takeIf { features.dice }?.let { roll ->
            // Remonte l'overlay à chaque nouveau secouage : sans cette clé, Compose recompose
            // l'instance existante et les dés 3D restent figés sur le lancer précédent.
            key(roll.rollId) {
                DiceShakeOverlay(
                    dieConfig = roll.dieConfig,
                    count = roll.count,
                    onRolled = { results ->
                        shakeRollRolling = false
                        viewModel.createDiceRollEvent(
                            characterName = roll.characterName,
                            dieType = roll.dieConfig.type,
                            count = roll.count,
                            results = results,
                            modifier = roll.modifier
                        )
                    },
                    rollModifier = roll.modifier,
                    totalEnabled = diceTotalEnabled,
                    faceStyle = diceFaceStyle,
                    onDismiss = {
                        shakeRollResult = null
                        shakeRollRolling = false
                    },
                    modifier = Modifier.fillMaxSize()
                )
            }
        }

        // Bulles de minuteurs : déclarées en dernier pour rester visibles par-dessus tous les
        // overlays de l'écran, au même emplacement que les autres bulles (hors barre de navigation)
        PlayerTimerBubbles(
            timers = playerActiveTimers,
            onExpired = { timerViewModel.removeExpiredTimer(it) },
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .padding(navigationPadding)
        )
    }
}

@Composable
private fun PlayerTabContent(
    selectedTab: PlayerTab,
    table: GameTable,
    tableMembers: List<TableMember>,
    characters: List<Character>,
    currentCharacter: Character?,
    currentUserId: String,
    universeId: String,
    isFreeConstruction: Boolean,
    isCharacterSheetAvailable: Boolean,
    messages: List<GameEvent>,
    requestedConversationKey: String?,
    onConversationRequestHandled: () -> Unit,
    availableDice: List<DieConfig>,
    defaultDie: DieType,
    multiDiceEnabled: Boolean,
    modifiersEnabled: Boolean,
    totalEnabled: Boolean,
    faceStyle: DiceFaceStyle,
    defaultDiceCount: Int,
    defaultModifier: Int,
    diceConfigLoaded: Boolean,
    shakeRollActive: Boolean,
    onNavigateBack: () -> Unit,
    onLeaveTable: () -> Unit,
    onSendMessage: (text: String, recipientIds: List<String>, characterName: String) -> Unit,
    onSendAlert: (gmId: String, characterName: String, alertType: AlertType, message: String?) -> Unit,
    onRollCompleted: (dieConfig: DieConfig, count: Int, results: List<Int>, modifier: Int) -> Unit,
    onDiceConfigChanged: (dieConfig: DieConfig, count: Int, modifier: Int) -> Unit,
    modifier: Modifier = Modifier
) {
    AnimatedContent(
        targetState = selectedTab,
        transitionSpec = {
            val direction = if (targetState.ordinal > initialState.ordinal) 1 else -1
            (slideInHorizontally(tween(200)) { it * direction } + fadeIn(tween(200))) togetherWith
                    (slideOutHorizontally(tween(200)) { it * -direction } + fadeOut(tween(200)))
        },
        label = "TabTransition"
    ) { tab ->
        when (tab) {
            PlayerTab.TABLE -> PlayerTableTab(
                table = table,
                tableMembers = tableMembers,
                characters = characters,
                currentUserId = currentUserId,
                onNavigateBack = onNavigateBack,
                onLeaveTable = onLeaveTable,
                modifier = modifier.padding(horizontal = 16.dp, vertical = 8.dp)
            )

            PlayerTab.CHARACTER -> CharacterTab(
                character = currentCharacter,
                isFreeConstruction = isFreeConstruction,
                isCharacterSheetAvailable = isCharacterSheetAvailable,
                tableId = table.id,
                userId = currentUserId,
                universeId = universeId,
                modifier = modifier
            )

            PlayerTab.INVENTORY -> {
                if (currentCharacter != null) {
                    val inventoryViewModel: InventoryViewModel = koinViewModel {
                        parametersOf(currentCharacter.id, table.id, currentUserId)
                    }
                    InventoryTab(
                        viewModel = inventoryViewModel,
                        ownerName = currentCharacter.name,
                        tableMembers = tableMembers.filter {
                            it.role == MemberRole.PLAYER && it.userId != currentUserId
                        },
                        characters = characters,
                        modifier = modifier
                    )
                }
            }

            PlayerTab.CONVERSATIONS -> {
                val characterName = currentCharacter?.name ?: currentUserId
                val gmId = remember(tableMembers) {
                    tableMembers.firstOrNull { it.role == MemberRole.GAME_MASTER }?.userId.orEmpty()
                }
                MessagesTab(
                    messages = messages,
                    tableMembers = tableMembers,
                    characters = characters,
                    currentUserId = currentUserId,
                    isGm = false,
                    onSendMessage = { text, ids -> onSendMessage(text, ids, characterName) },
                    onSendAlert = { alertType, message -> onSendAlert(gmId, characterName, alertType, message) },
                    requestedConversationKey = requestedConversationKey,
                    onConversationRequestHandled = onConversationRequestHandled,
                    modifier = modifier
                )
            }

            PlayerTab.DICE -> {
                if (diceConfigLoaded) {
                    DiceTab(
                        availableDice = availableDice,
                        defaultDie = defaultDie,
                        multiDiceEnabled = multiDiceEnabled,
                        modifiersEnabled = modifiersEnabled,
                        totalEnabled = totalEnabled,
                        faceStyle = faceStyle,
                        defaultDiceCount = defaultDiceCount,
                        defaultModifier = defaultModifier,
                        shakeRollActive = shakeRollActive,
                        onRollCompleted = onRollCompleted,
                        onConfigChanged = onDiceConfigChanged,
                        modifier = modifier
                    )
                } else {
                    Box(modifier = modifier.fillMaxSize())
                }
            }
        }
    }
}

@Composable
private fun PlayerTimerBubbles(
    timers: List<ActiveTimer>,
    onExpired: (timerId: String) -> Unit,
    modifier: Modifier = Modifier
) {
    BoxWithConstraints(modifier) {
        val cW = constraints.maxWidth
        val cH = constraints.maxHeight
        timers.forEachIndexed { index, timer ->
            key(timer.id) {
                DraggableBubble(
                    alignment = Alignment.BottomEnd,
                    bottomPadding = (100 + index * 60).dp,
                    containerWidth = cW,
                    containerHeight = cH,
                    dragKey = timer.id
                ) {
                    TimerBubble(timer = timer, onExpired = { onExpired(timer.id) })
                }
            }
        }
    }
}

@Composable
private fun BoxScope.PlayerBubbleOverlays(
    activeAnnouncements: List<ActiveAnnouncement>,
    minimizedAnnouncementIds: Set<String>,
    playerActiveDigicode: ActiveDigicode?,
    isDigicodeMinimized: Boolean,
    playerActiveLockpicking: ActiveLockpicking?,
    isLockpickingMinimized: Boolean,
    whiteboardState: WhiteboardState,
    availableModules: Set<GameModule>,
    combatIsMinimized: Boolean,
    combatActiveParticipant: CombatParticipant?,
    combatCharacters: List<Character>,
    onRestoreAnnouncement: (announcementEventId: String) -> Unit,
    onRestoreDigicode: () -> Unit,
    onRestoreLockpicking: () -> Unit,
    onRestoreWhiteboard: () -> Unit,
    onRestoreCombat: () -> Unit
) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        val cW = constraints.maxWidth
        val cH = constraints.maxHeight

        var leftBubbleIndex = 0
        activeAnnouncements.filter { it.eventId in minimizedAnnouncementIds }
            .forEach { announcement ->
                val idx = leftBubbleIndex++
                key(announcement.eventId) {
                    DraggableBubble(
                        alignment = Alignment.BottomStart,
                        bottomPadding = (100 + idx * 60).dp,
                        containerWidth = cW,
                        containerHeight = cH,
                        dragKey = announcement.eventId
                    ) {
                        AnnouncementBubble(
                            onClick = { onRestoreAnnouncement(announcement.eventId) },
                            imageUrl = announcement.imageUrl
                        )
                    }
                }
            }

        if (GameModule.DIGICODE in availableModules && playerActiveDigicode != null && isDigicodeMinimized) {
            val idx = leftBubbleIndex++
            key(playerActiveDigicode.id) {
                DraggableBubble(
                    alignment = Alignment.BottomStart,
                    bottomPadding = (100 + idx * 60).dp,
                    containerWidth = cW,
                    containerHeight = cH,
                    dragKey = playerActiveDigicode.id
                ) {
                    DigicodeBubble(onClick = onRestoreDigicode)
                }
            }
        }

        if (GameModule.LOCKPICKING in availableModules && playerActiveLockpicking != null && isLockpickingMinimized) {
            val idx = leftBubbleIndex++
            key(playerActiveLockpicking.id) {
                DraggableBubble(
                    alignment = Alignment.BottomStart,
                    bottomPadding = (100 + idx * 60).dp,
                    containerWidth = cW,
                    containerHeight = cH,
                    dragKey = playerActiveLockpicking.id
                ) {
                    LockpickingBubble(onClick = onRestoreLockpicking)
                }
            }
        }

        if (whiteboardState.isMinimized) {
            val idx = leftBubbleIndex++
            DraggableBubble(
                alignment = Alignment.BottomStart,
                bottomPadding = (100 + idx * 60).dp,
                containerWidth = cW,
                containerHeight = cH,
                dragKey = Unit
            ) {
                WhiteboardBubble(onClick = onRestoreWhiteboard)
            }
        }

        if (combatIsMinimized) {
            val idx = leftBubbleIndex++
            DraggableBubble(
                alignment = Alignment.BottomStart,
                bottomPadding = (100 + idx * 60).dp,
                containerWidth = cW,
                containerHeight = cH,
                dragKey = "combat"
            ) {
                CombatBubble(
                    activeParticipant = combatActiveParticipant,
                    characters = combatCharacters,
                    onClick = onRestoreCombat
                )
            }
        }
    }
}
